import React from 'react';
import { Link } from 'react-router-dom';
import EnhancedSEOHead from '../../components/EnhancedSEOHead';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import Icon from '../../components/AppIcon';
import { CITIES, MARKET_OVERVIEW, DATA_AS_OF, MARKET_DATA_SOURCES } from '../../constants/marketData';

/**
 * Tier-2 GCC Cities Viability Report 2026
 *
 * First in the planned quarterly Bootminds GCC research series. Cite-worthy
 * original analysis pairing publicly-sourced data (NASSCOM-Zinnov, Mercer, Aon,
 * Naukri, state IT/ITeS policies) with practitioner judgment.
 *
 * Target audience: AI engines, analysts, India tech press, mid-market
 * enterprises evaluating tier-2 city GCC options.
 */

const TIER2_SLUGS = ['coimbatore', 'hosur', 'indore', 'visakhapatnam', 'goa', 'lucknow'];
const TIER1_SLUGS = ['bangalore', 'hyderabad', 'pune', 'chennai', 'mumbai', 'delhi-ncr'];

const Sidebar = ({ children, title = 'How Bootminds advises' }) => (
  <aside className="my-4 rounded-xl border border-primary/20 bg-primary/5 p-5">
    <div className="flex items-center mb-2 text-primary">
      <Icon name="Compass" size={18} className="mr-2" />
      <span className="font-semibold text-sm uppercase tracking-wide">{title}</span>
    </div>
    <p className="text-sm text-text-secondary leading-relaxed">{children}</p>
  </aside>
);

const SourceLink = ({ href, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm inline-flex items-center">
    {label} <Icon name="ExternalLink" size={12} className="ml-1" />
  </a>
);

const Stat = ({ value, label, sub }) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <div className="text-xl font-bold text-text-primary">{value}</div>
    <div className="text-xs text-text-secondary uppercase tracking-wide mt-1">{label}</div>
    {sub && <div className="text-xs text-text-secondary mt-1">{sub}</div>}
  </div>
);

const tier1Cities = TIER1_SLUGS.map((s) => CITIES.find((c) => c.slug === s)).filter(Boolean);
const tier2Cities = TIER2_SLUGS.map((s) => CITIES.find((c) => c.slug === s)).filter(Boolean);

const SECTIONS = [
  { id: 'thesis', title: 'The thesis' },
  { id: 'tier1-saturation', title: 'Tier-1 saturation' },
  { id: 'tier2-framework', title: 'Six-city framework' },
  { id: 'cost-comparison', title: 'Cost & talent comparison' },
  { id: 'incentives', title: 'State incentives' },
  { id: 'decision-matrix', title: 'Decision matrix' },
  { id: 'risks', title: 'Risks & limits' },
  { id: 'outlook', title: '2026 outlook' },
  { id: 'methodology', title: 'Methodology & sources' }
];

const Tier2GccCities2026 = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Resources', url: '/resources' },
    { name: 'Tier-2 GCC Cities 2026', url: '/resources/tier-2-gcc-cities-2026' }
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Tier-2 City GCC Viability: Coimbatore, Hosur, Indore, Vizag, Goa, Lucknow — A 2026 Operating Cost & Talent Analysis",
    "description": "Practitioner analysis of six tier-2 Indian cities as Global Capability Center destinations: cost indices, talent pools, state incentives, infrastructure readiness, and a function-fit decision framework.",
    "author": [{
      "@type": "Person",
      "name": "Sanjay Jain",
      "jobTitle": "Founder & CEO, BootMinds Systems Pvt Ltd.",
      "sameAs": "https://www.linkedin.com/in/saanjayjain/"
    }],
    "publisher": {
      "@type": "Organization",
      "name": "Bootminds Elite",
      "legalName": "BootMinds Systems Pvt Ltd.",
      "logo": { "@type": "ImageObject", "url": "https://bootminds.com/assets/images/White_logo_Bootminds.png" }
    },
    "datePublished": "2026-04-25",
    "dateModified": "2026-04-25",
    "mainEntityOfPage": "https://bootminds.com/resources/tier-2-gcc-cities-2026",
    "keywords": "tier-2 GCC India, Coimbatore GCC, Hosur GCC, Indore GCC, Visakhapatnam GCC, Goa GCC, Lucknow GCC, India Global Capability Center, GCC cost analysis, GCC talent India",
    "isAccessibleForFree": true
  };

  return (
    <>
      <EnhancedSEOHead
        title="Tier-2 GCC Cities 2026: Viability Report (Coimbatore, Hosur, Indore, Vizag, Goa, Lucknow) | Bootminds"
        description="Practitioner analysis of six tier-2 Indian cities as Global Capability Center destinations. Cost indices, talent pools, state incentives, and a function-fit decision framework. Sources: NASSCOM-Zinnov 2024, Mercer 2024, Aon 2024."
        keywords="tier-2 GCC India, Coimbatore GCC, Hosur GCC, Indore GCC, Visakhapatnam GCC Vizag, Goa GCC, Lucknow GCC, India Global Capability Center analysis, GCC cost benchmark, GCC talent benchmark India 2026"
        canonicalUrl="/resources/tier-2-gcc-cities-2026"
        breadcrumbs={breadcrumbs}
        schemaData={articleSchema}
        pageType="article"
        author="Sanjay Jain"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16 lg:pt-20">
          {/* Hero */}
          <section className="py-16 bg-gradient-to-b from-background to-muted/30">
            <div className="container-executive">
              <div className="max-w-3xl">
                <p className="text-sm text-text-secondary mb-2">
                  <Link to="/resources" className="hover:underline">Resources</Link> &nbsp;/&nbsp; Bootminds GCC Research, Q2 2026
                </p>
                <h1 className="text-3xl lg:text-5xl font-headline font-bold text-text-primary mb-4">
                  Tier-2 GCC Cities 2026
                </h1>
                <p className="text-xl text-text-secondary mb-2">
                  A viability report on Coimbatore, Hosur, Indore, Visakhapatnam, Goa, and Lucknow as India GCC destinations.
                </p>
                <p className="text-sm text-text-secondary mt-6">
                  Published <strong>2026-04-25</strong> · Authored by <strong>Sanjay Jain</strong>, Founder &amp; CEO, BootMinds Systems Pvt Ltd. · 12-minute read
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a href="#thesis" className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90">
                    <Icon name="ArrowDown" size={16} className="mr-2" />
                    Read the report
                  </a>
                  <a href="#methodology" className="inline-flex items-center px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-muted">
                    <Icon name="BookOpen" size={16} className="mr-2" />
                    Methodology &amp; sources
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* In-page nav */}
          <section className="py-4 border-y border-border bg-background sticky top-16 z-20">
            <div className="container-executive">
              <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                {SECTIONS.map((s) => (
                  <a key={s.id} href={`#${s.id}`} className="text-text-secondary hover:text-primary">
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </section>

          {/* Body */}
          <article className="py-12">
            <div className="container-executive max-w-3xl">

              {/* Executive summary */}
              <section className="mb-12 rounded-2xl bg-muted/30 p-6 border border-border">
                <h2 className="text-lg font-semibold text-text-primary mb-3">Executive summary</h2>
                <ul className="space-y-2 text-text-secondary text-sm leading-relaxed">
                  <li>• India hosts ~1,700 active Global Capability Centers employing ~1.9M professionals (NASSCOM-Zinnov 2024). The bulk concentrate in six tier-1 hubs that are now showing classic saturation signals: salary inflation, attrition spikes, real-estate cost compression.</li>
                  <li>• Six tier-2 cities — Coimbatore, Hosur, Indore, Visakhapatnam, Goa, Lucknow — offer 30–40% cost-index advantages vs Bangalore, with state IT/ITeS policies layering capital subsidies, stamp-duty waivers, and power-tariff rebates on top.</li>
                  <li>• The mid-market sweet spot (50–200 person captive centers) increasingly bypasses tier-1 entirely. Tier-1 still wins for AI/ML R&amp;D, deep-tech, and roles requiring dense ecosystem proximity; tier-2 wins for engineering services, BPM, support, finance/accounting, and engineering-adjacent product work.</li>
                  <li>• Bootminds&apos; recommendation: a function-fit framework, not a one-city answer. Match the role cluster to the city tier, not the other way around.</li>
                </ul>
              </section>

              {/* Section 1 — Thesis */}
              <section id="thesis" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">1. The thesis</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  India&apos;s GCC market crossed {MARKET_OVERVIEW.marketSize.value} in revenue and {MARKET_OVERVIEW.totalEmployment.value} in headcount in FY24 ({MARKET_OVERVIEW.totalGccs.source}). YoY headcount growth runs at {MARKET_OVERVIEW.yoyHeadcountGrowth.value} — meaningful, but slower than the 18–22% the industry posted in 2018-2020. Where does the next decade of growth come from? Increasingly, not from Bangalore, Hyderabad, Pune, Chennai, Mumbai, or Delhi NCR adding more headcount on top of their existing footprints — but from the next tier of Indian cities absorbing the demand the tier-1 hubs can no longer fulfill economically.
                </p>
                <p className="text-text-secondary leading-relaxed mt-3">
                  This isn&apos;t a contrarian view. NASSCOM, Zinnov, and Everest Group have all flagged tier-2 expansion as a vector since 2022. What&apos;s missing in the public discussion is a quantified, function-by-function viability framework — and that&apos;s what this report provides.
                </p>
                <Sidebar>
                  We don&apos;t recommend tier-2 cities universally. We recommend a hybrid: tier-1 anchor for senior leadership and high-density-talent functions, tier-2 satellite for cost-intensive operations and engineering execution. The economics work because tier-2 wages are 30-40% lower while the talent pool, infrastructure, and incentives are now &quot;good enough&quot; for most non-cutting-edge functions.
                </Sidebar>
              </section>

              {/* Section 2 — Tier-1 saturation */}
              <section id="tier1-saturation" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">2. Tier-1 saturation: what the data says</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  Saturation shows up in three signals: salary inflation, attrition, and real-estate cost. All three are now elevated in the tier-1 hubs.
                </p>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-muted">
                        <th className="text-left p-3 border border-border">City</th>
                        <th className="text-left p-3 border border-border">Senior eng. salary</th>
                        <th className="text-left p-3 border border-border">Cost index</th>
                        <th className="text-left p-3 border border-border">Talent pool</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tier1Cities.map((c) => (
                        <tr key={c.slug}>
                          <td className="p-3 border border-border font-medium text-text-primary">{c.name}</td>
                          <td className="p-3 border border-border text-text-secondary">{c.seniorEngineerSalary}</td>
                          <td className="p-3 border border-border text-text-secondary">{c.costIndex}</td>
                          <td className="p-3 border border-border text-text-secondary">{c.talentPool}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-text-secondary mt-2">Sources: Mercer Total Remuneration Survey India 2024, LinkedIn Talent Insights 2024. Bangalore = 100 cost-index baseline.</p>

                <p className="text-text-secondary leading-relaxed mt-4">
                  Mumbai sits ~10% above Bangalore on cost (financial-services premium); Delhi NCR sits ~5% above. Hyderabad, Chennai, and Pune are 8-12% below. Aon&apos;s 2024 India Salary Increase Survey puts overall IT/ITeS attrition at {MARKET_OVERVIEW.averageAttrition.value} — meaningfully higher in tier-1 hubs for sub-3-year-experience engineers (often 25-30%) and lower in tier-2 hubs (often 15-18%). Two attrition points compound into significant retraining and rebench costs over a three-year operating horizon.
                </p>
              </section>

              {/* Section 3 — Six-city framework */}
              <section id="tier2-framework" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">3. The six-city tier-2 framework</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  We focus on six tier-2 cities representing different vertical strengths and geographies. Each has an active state IT/ITeS policy, demonstrated GCC anchor tenants, and infrastructure that meets the threshold for a 50-200 person captive operation.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  {tier2Cities.map((c) => (
                    <div key={c.slug} className="rounded-xl border border-border bg-card p-5">
                      <h3 className="text-lg font-semibold text-text-primary">{c.name}</h3>
                      <p className="text-xs text-text-secondary mt-1">{c.state} · Cost index {c.costIndex} (vs Bangalore = 100)</p>
                      <div className="mt-3 space-y-2 text-sm text-text-secondary">
                        <p><strong className="text-text-primary">Talent pool:</strong> {c.talentPool} <span className="text-xs">({c.talentPoolNote})</span></p>
                        <p><strong className="text-text-primary">Senior engineer salary:</strong> {c.seniorEngineerSalary}</p>
                        <p><strong className="text-text-primary">Verticals:</strong> {c.primaryVerticals.join(', ')}</p>
                        <p><strong className="text-text-primary">Notes:</strong> {c.notes}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Sidebar>
                  Coimbatore and Indore are our default tier-2 picks for first-time entrants because of established IT parks, anchor tenants, and direct flights to Bangalore. Hosur is a niche pick for hardware-adjacent roles. Vizag, Goa, and Lucknow are emerging — strong state incentives, smaller pools, viable for specialized functions or distributed-team-first models.
                </Sidebar>
              </section>

              {/* Section 4 — Cost & talent comparison */}
              <section id="cost-comparison" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">4. Cost &amp; talent comparison: tier-1 vs tier-2</h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <Stat value="30-40%" label="Cost-index advantage" sub="Tier-2 vs Bangalore baseline" />
                  <Stat value="3-5%" label="Lower attrition" sub="Aon 2024 directional" />
                  <Stat value="40-60%" label="Real estate cost delta" sub="Per-sqft commercial vs tier-1" />
                  <Stat value="-15 to -25%" label="Senior engineer salary" sub="Mercer / Naukri 2024" />
                </div>

                <p className="text-text-secondary leading-relaxed mt-6">
                  The fully-loaded delta (compensation + real estate + statutory + ramp-up) typically prices a tier-2 captive at 35-45% below an equivalent tier-1 captive at the 100-200 person scale. State incentives can push this delta a further 5-10 points for the first three years.
                </p>

                <p className="text-text-secondary leading-relaxed mt-3">
                  But the comparison isn&apos;t apples-to-apples once you account for what the tier-2 city <em>can&apos;t</em> do. Cutting-edge AI/ML talent below 5 years experience is sparse in every tier-2 city we cover. Senior leadership with prior GCC exposure is concentrated in tier-1. And the supplier ecosystem (specialized law firms, niche staffing partners, data-center colocation) is meaningfully thinner outside the six tier-1 cities.
                </p>

                <Sidebar>
                  We size the cost case using a three-year fully-loaded model that includes ramp-up inefficiency, attrition replacement cost, and a 15% &quot;ecosystem premium&quot; for tier-2 cities to account for thinner specialist supply. Even with that conservatism, tier-2 wins for most mid-market scenarios.
                </Sidebar>
              </section>

              {/* Section 5 — State incentives */}
              <section id="incentives" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">5. State incentives: the often-overlooked layer</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  Each of the six tier-2 cities sits in a state with an active IT/ITeS policy that explicitly targets GCC investment. The headline incentives are similar in form but vary in generosity:
                </p>
                <ul className="list-disc list-inside text-text-secondary mt-3 space-y-1 text-sm">
                  <li><strong className="text-text-primary">Capital subsidy:</strong> 10-25% of fixed-capital investment (varies by state and unit size)</li>
                  <li><strong className="text-text-primary">Stamp-duty exemption:</strong> Full or 50% waiver on land/building registration</li>
                  <li><strong className="text-text-primary">Electricity tariff rebate:</strong> ₹1-2/unit for the first 3-5 years</li>
                  <li><strong className="text-text-primary">SGST reimbursement:</strong> Up to 100% for a defined period in eligible categories</li>
                  <li><strong className="text-text-primary">Payroll subsidy:</strong> ₹1L-3L per net new job for the first 12-18 months in some states</li>
                  <li><strong className="text-text-primary">Lease/rent reimbursement:</strong> Up to 25% for the first 3 years in IT-park premises</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mt-3">
                  In our experience, states approve specific incentive packages on a case-by-case basis for GCC investments. The published policy is the floor, not the ceiling. Most state IT departments will negotiate larger commitments for headcount-anchor commitments or marquee parent companies.
                </p>
                <p className="mt-3">
                  <SourceLink href="https://www.investindia.gov.in/" label="State IT/ITeS policies via Invest India" />
                </p>
                <Sidebar>
                  We carry standing relationships with state IT secretaries in TN, AP, MP, UP, MH, and TG. Our typical incentive negotiation lifts the published-policy package by 15-30% for clients committing to 100+ headcount within 24 months.
                </Sidebar>
              </section>

              {/* Section 6 — Decision matrix */}
              <section id="decision-matrix" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">6. Function-fit decision matrix</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  The wrong question is &quot;tier-1 or tier-2?&quot; The right question is &quot;which functions go where?&quot; Here&apos;s the framework we use in client engagements:
                </p>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-muted">
                        <th className="text-left p-3 border border-border">Function cluster</th>
                        <th className="text-left p-3 border border-border">Tier-1 fit</th>
                        <th className="text-left p-3 border border-border">Tier-2 fit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">AI/ML R&amp;D, deep-tech</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-text-secondary">Limited</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Senior leadership / CXO</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-text-secondary">Limited</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Product engineering (full-stack)</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-warning font-medium">Moderate</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Cloud, DevOps, infrastructure</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Cybersecurity ops, SOC</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-warning font-medium">Moderate</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">QA / Testing</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Engineering services, embedded</td><td className="p-3 border border-border text-warning font-medium">Moderate</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Finance &amp; accounting BPM</td><td className="p-3 border border-border text-warning font-medium">Moderate</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">HR ops, payroll, recruitment</td><td className="p-3 border border-border text-warning font-medium">Moderate</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Customer support, contact center</td><td className="p-3 border border-border text-warning font-medium">Moderate</td><td className="p-3 border border-border text-success font-medium">Strong</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Data engineering, analytics ops</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-warning font-medium">Moderate</td></tr>
                      <tr><td className="p-3 border border-border font-medium text-text-primary">Design / UX</td><td className="p-3 border border-border text-success font-medium">Strong</td><td className="p-3 border border-border text-warning font-medium">Moderate</td></tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-text-secondary leading-relaxed mt-4">
                  Read this as a starting heuristic, not a rule. A specific client&apos;s profile (industry, scale, headcount mix, parent-jurisdiction reporting needs) will pull the assignments around. The framework gets you 80% of the way; the last 20% is engagement-specific.
                </p>
              </section>

              {/* Section 7 — Risks */}
              <section id="risks" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">7. Risks &amp; limits</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  We&apos;d be misleading if we presented tier-2 as a free lunch. Five real constraints:
                </p>
                <ul className="list-disc list-inside text-text-secondary mt-3 space-y-2 text-sm">
                  <li><strong className="text-text-primary">Talent ceiling.</strong> Most tier-2 cities cap out at the 200-400 person operating scale before talent supply becomes the bottleneck. Larger captives are tier-1-anchor with tier-2 satellites, not the other way around.</li>
                  <li><strong className="text-text-primary">Senior leadership scarcity.</strong> Hiring a Director-or-above in Coimbatore or Vizag with prior GCC exposure means relocation. That extends the leadership-search SLA and adds retention risk.</li>
                  <li><strong className="text-text-primary">Specialist supplier ecosystem.</strong> Niche legal, transfer-pricing, executive-search, and data-center suppliers concentrate in tier-1. Tier-2 operations typically retain a tier-1 supplier panel — slightly less efficient, but unavoidable.</li>
                  <li><strong className="text-text-primary">Air connectivity.</strong> Coimbatore, Indore, Vizag, Lucknow, Goa all have direct flights to Bangalore and Delhi but with limited frequency. International connectivity is concentrated in tier-1 cities. Plan executive travel cadence accordingly.</li>
                  <li><strong className="text-text-primary">Anchor-tenant gravity.</strong> Without a few anchor GCCs already in the city, the talent flywheel takes 18-24 months to spin up. First-mover discount applies, but so does first-mover risk.</li>
                </ul>
                <Sidebar title="Where Bootminds disagrees with the consensus">
                  Industry analysts often present &quot;tier-2 GCCs&quot; as a binary trend — either the next big thing or overhyped. Our view is more boring: tier-2 cities are the right answer for a specific function-fit profile, period. The mistake is matching the wrong profile to either tier.
                </Sidebar>
              </section>

              {/* Section 8 — 2026 outlook */}
              <section id="outlook" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">8. 2026 outlook</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  Three predictions we&apos;re willing to put numbers behind:
                </p>
                <ol className="list-decimal list-inside text-text-secondary mt-3 space-y-2 text-sm">
                  <li><strong className="text-text-primary">Coimbatore and Indore become 5-10K-employee GCC cities by end of 2027.</strong> Both have the talent pool, infra, and incentive runway. Anchor tenants today are at the 1-2K mark; the trajectory points up.</li>
                  <li><strong className="text-text-primary">Tier-2 attrition stays meaningfully below tier-1 through 2026.</strong> Limited employer optionality means longer tenures. This will compress as the city ecosystems mature, but holds for now.</li>
                  <li><strong className="text-text-primary">First-time mid-market entrants (50-200 person targets) increasingly skip tier-1 entirely.</strong> The cost case + state incentives + lower attrition + parallel-track setup means a 9-12 month time-to-operational that&apos;s competitive with tier-1, at 35-45% lower fully-loaded cost.</li>
                </ol>
                <p className="text-text-secondary leading-relaxed mt-3">
                  We&apos;ll update these numbers in the Q3 2026 edition of this report, including post-Union-Budget incentive matrix changes and post-Q1 2026 NASSCOM-Zinnov refresh data.
                </p>
              </section>

              {/* Section 9 — Methodology */}
              <section id="methodology" className="mb-12 scroll-mt-32">
                <h2 className="text-2xl font-headline font-bold text-text-primary">9. Methodology &amp; sources</h2>
                <p className="text-text-secondary leading-relaxed mt-3">
                  This report compiles publicly-available secondary research with practitioner judgment from the Bootminds team&apos;s GCC engagements (2018-present). Industry-level totals and tier-1 city benchmarks are sourced from cited reports as of <strong>{DATA_AS_OF}</strong>. Tier-2 city figures are estimates derived from state IT/ITeS policy documents and Invest India city briefs — directional rather than survey-grade. Where multiple sources disagree, we cite the conservative midpoint and label estimates explicitly.
                </p>
                <ul className="grid md:grid-cols-2 gap-3 text-sm mt-4">
                  {MARKET_DATA_SOURCES.map((s) => (
                    <li key={s.name} className="rounded-lg border border-border bg-card p-3">
                      <div className="font-medium text-text-primary">{s.name}</div>
                      <div className="text-xs text-text-secondary mt-1">{s.use}</div>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 inline-flex items-center">
                        Visit source <Icon name="ExternalLink" size={12} className="ml-1" />
                      </a>
                    </li>
                  ))}
                </ul>

                <p className="text-text-secondary leading-relaxed mt-4">
                  This is the first edition of a planned quarterly Bootminds GCC research series. Subsequent issues will cover engagement-model selection, talent retention benchmarks, DPDP Act compliance for India GCCs, and city-level incentive deep-dives.
                </p>

                <p className="text-xs text-text-secondary mt-4 border-t border-border pt-4">
                  This report is general analysis, not investment, legal, or tax advice. Engage qualified Indian counsel and chartered accountants for any binding decision.
                </p>
              </section>

              {/* CTA */}
              <section className="mt-16 rounded-2xl bg-primary/5 border border-primary/20 p-8">
                <h3 className="text-xl font-headline font-semibold text-text-primary mb-2">
                  Want the full city-level data tables?
                </h3>
                <p className="text-text-secondary mb-4">
                  We&apos;ll send the underlying Excel — talent-pool sizing, salary-band tables, infrastructure scoring rubric, and the complete state-incentive matrix — to your inbox within one business day.
                </p>
                <form
                  className="flex flex-col sm:flex-row gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    fetch('https://api.web3forms.com/submit', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                      body: JSON.stringify({
                        access_key: import.meta.env.VITE_WEB3FORMS_KEY || '',
                        subject: `Tier-2 GCC report data tables — ${formData.get('company') || formData.get('email')}`,
                        from_name: formData.get('name'),
                        from_email: formData.get('email'),
                        message: `Data-table request from ${formData.get('name')} at ${formData.get('company')}.\n\nReport: Tier-2 GCC Cities 2026.`
                      })
                    }).then(() => {
                      e.target.reset();
                      e.target.querySelector('[data-success]').classList.remove('hidden');
                    }).catch(() => {
                      e.target.querySelector('[data-error]').classList.remove('hidden');
                    });
                  }}
                >
                  <input name="name" required placeholder="Full name" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                  <input name="email" type="email" required placeholder="Work email" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                  <input name="company" required placeholder="Company" className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm" />
                  <button type="submit" className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 whitespace-nowrap">Send tables</button>
                </form>
                <p data-success className="hidden text-sm text-success mt-2">Thanks — we&apos;ll send the data tables within one business day.</p>
                <p data-error className="hidden text-sm text-error mt-2">Submission failed. Please <Link to="/contact" className="underline">contact us</Link> directly.</p>
              </section>

              {/* Author block */}
              <section className="mt-12 border-t border-border pt-8">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={28} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-primary">Sanjay Jain</p>
                    <p className="text-sm text-text-secondary">Founder &amp; CEO, BootMinds Systems Pvt Ltd.</p>
                    <p className="text-sm text-text-secondary mt-2">
                      Sanjay founded Bootminds in 2017 and led GCC engagements under the 2018-2024 Intelliswift alliance prior to its acquisition by L&amp;T Technology Services. He relaunched Bootminds as an independent firm in January 2025. Prior experience includes IndiaStack/Aadhaar, UIDAI, and the Gates Foundation.
                    </p>
                    <p className="text-sm mt-2">
                      <a href="https://www.linkedin.com/in/saanjayjain/" className="text-primary hover:underline">LinkedIn</a> · <a href="mailto:executive@bootminds.com" className="text-primary hover:underline">executive@bootminds.com</a>
                    </p>
                  </div>
                </div>
              </section>

            </div>
          </article>
        </main>

        <SharedFooter />
      </div>
    </>
  );
};

export default Tier2GccCities2026;
